import { useApp } from '../../app/AppContext'
import { useI18n } from './useI18n'

const FALLBACK_CATEGORY = 'unit'

/**
 * Hook providing a category-aware translation function for `product_category_strings`.
 *
 * Returns `productsCategoryString(action, category?, vars?)` which resolves i18n key
 * `product_category_strings.{action}_{category}`.
 *
 * Category resolution order:
 *   1. Explicit `category` argument
 *   2. `panelSettings.default_product_category.category`
 *   3. `'unit'` hardcoded fallback
 *
 * If `vars.count` is provided the plural form is selected automatically
 * (_one / _few / _many suffix appended by `i18n.plural`).
 *
 * Special case — `action = null | undefined`:
 *   Key becomes `product_category_strings.{category}` (no prefix).
 *   Use this to resolve plain noun count forms: `{category}_one/few/many`.
 *
 * @returns {(action: string|null, category?: string|null, vars?: object) => string}
 *
 * @example
 * const productsCategoryString = useProductStrings()
 *
 * productsCategoryString('find')                                         // "Znajdź dom"  (default from settings)
 * productsCategoryString('hidden_by_filter', product.product_category)  // "Mieszkanie ukryte przez filtr"
 * productsCategoryString('available_count', 'unit', { count: 3 })        // "3 dostępne lokale"
 * productsCategoryString(null, 'unit', { count: 5 })                     // "Lokali"  (plain noun form)
 */
export function useProductStrings() {
  const { panelSettings } = useApp()
  const { t, plural } = useI18n()
  const defaultCategory = panelSettings?.default_product_category?.category ?? FALLBACK_CATEGORY

  return function productsCategoryString(action, category, vars = {}) {
    const useDefaultCategory = !category || category === 'default'
    const cat = useDefaultCategory ? defaultCategory : category
    const key = action
      ? `product_category_strings.${action}_${cat}`
      : `product_category_strings.${cat}`

    if (vars.count !== undefined) {
      return plural(key, vars.count, vars)
    }
    return t(key, vars)
  }
}
